import { FC, useRef, useState, useMemo } from 'react'
import { Box, Autocomplete, TextField, Chip, Paper, MenuItem, Popper, ClickAwayListener } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { toast } from 'react-toastify'

interface CustomAutoCompleteProps {
  inputValue: string
  setInputValue: (v: string) => void
  batchNumbers: string[]
  setBatchNumbers: (batchNumbers: string[]) => void
  batchNumbersWithCertificates?: string[]
  onChange?: (v: string[]) => void
  disabled?: boolean
}

export const CustomAutoComplete: FC<CustomAutoCompleteProps> = ({
  inputValue,
  setInputValue,
  batchNumbers,
  setBatchNumbers,
  batchNumbersWithCertificates = [],
  onChange,
  disabled,
}) => {
  const { t } = useTranslation(['common', 'position', 'component'])
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const inputRef = useRef<HTMLDivElement>(null)

  const filteredBatchNumbers = useMemo(() => {
    if (!inputValue.trim()) {
      return batchNumbersWithCertificates
    }
    const lowerInput = inputValue.toLowerCase().trim()
    return batchNumbersWithCertificates.filter(batchNumber => batchNumber.toLowerCase().includes(lowerInput))
  }, [batchNumbersWithCertificates, inputValue])

  const cleanupBatchNumbers = (values: string[]): string[] => {
    return Array.from(new Set(values.map(v => v.trim()).filter(v => v !== '')))
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelectBatchNumber = (batchNumber: string) => {
    if (batchNumbers.includes(batchNumber)) {
      toast.warn(`"${batchNumber}" ${t('already-exists', { ns: 'position' })}`)
      return
    }
    setBatchNumbers([...batchNumbers, batchNumber])
    setInputValue('')
    setAnchorEl(null)
  }

  const handleInputClick = () => {
    setAnchorEl(inputRef.current)
  }

  return (
    <Box sx={{ mt: 1, ml: 1, mr: 1 }}>
      <Autocomplete<string, true, true, true>
        multiple
        freeSolo
        disabled={disabled}
        disableClearable
        options={[]}
        value={batchNumbers}
        filterSelectedOptions
        onChange={(_, newValue) => {
          setBatchNumbers(cleanupBatchNumbers(newValue))
          onChange?.(cleanupBatchNumbers(newValue))
        }}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          setInputValue(newInputValue)
          if (reason === 'input') {
            setAnchorEl(inputRef.current)
          }
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={option}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            ref={inputRef}
            variant="outlined"
            label={t('batchNumber', { ns: 'component' })}
            onClick={handleInputClick}
            onFocus={handleInputClick}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault()
                const currentValue = inputValue.trim()

                if (currentValue && batchNumbers.includes(currentValue)) {
                  toast.warn(`"${currentValue}" ${t('already-exists', { ns: 'position' })}`)
                }
              }
            }}
          />
        )}
      />
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={8}
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              minWidth: anchorEl?.offsetWidth || 200,
            }}
          >
            {filteredBatchNumbers.length > 0 ? (
              filteredBatchNumbers.map((batchNumber, index) => (
                <MenuItem
                  key={index}
                  onMouseDown={e => {
                    e.preventDefault()
                    handleSelectBatchNumber(batchNumber)
                  }}
                >
                  {batchNumber}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t('no-matching-items', { ns: 'position' })}</MenuItem>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
}
